import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {PublisherService} from "../../services/publisher.service";
import {ApplicationStatusEnum} from "../../models/application-status.enum";
import {toggleLoader} from "../../store/global/global-slice";
import {PublisherBookPropositionListModel} from "../../models/publisher/publisher-book-proposition-list.model";
import Grid from "@material-ui/core/Grid";
import {Tab, Tabs} from "@material-ui/core";
import {CustomTabPanel} from "../../components/ui-base/tab-panel";
import {StyledButton} from "../../components/ui-base/button";
import {EmptyBookShort} from "../../components/author/books/empty-book-short";
import {BookPropositionList} from "../../components/publisher/books-propositions/book-proposition-list";
import {
	ElasticFilterType,
	ElasticSearchSort,
	ElasticSearchType,
	SearchFilters,
	SearchForElastic
} from "../../components/publisher/books/search-for-elastic";
import {LastElasticSearchDataModel} from "../../models/view/last-elastic-search-data.model";

const PublisherBooksPropositions = () => {
	const [booksData, setBooksData] = useState<BooksData>({newestManuscripts: {}, bookmarkManuscripts: {}, notPublishManuscripts: {}, toPublishManuscripts: {}} as BooksData);
	const [lastSearchData, setLastSearchData] = useState<LastElasticSearchDataModel>({searchType: ElasticSearchType.searchTypeTitle, searchValue: '', sortField: ElasticSearchSort.submittedDate, sortValue: 'desc', filters: {} as SearchFilters});
	const [loaded, setLoaded] = useState(false);
	const [tabActive, setTabActive] = React.useState(0);
	const dispatch = useDispatch();
	const { t } = useTranslation(['publisher', 'common']);

	const loadNewestManuscripts = useCallback(async (
		sortField: ElasticSearchSort,
		sortValue: 'asc'|'desc',
		searchType: ElasticSearchType,
		searchValue: string,
		filters: SearchFilters,
		page = 1,
		perPage = 4
	) => {
		const newestManuscriptsLoaded = await PublisherService.getBooksPropositionsSearch(
			sortField,
			sortValue,
			searchType,
			searchValue,
			filters,
			page,
			perPage,
			[ApplicationStatusEnum.Submitted, ApplicationStatusEnum.OpenByPub],
			false
		);

		setBooksData(prevValue => ({
			...prevValue,
			newestManuscripts: {
				items: (page > 1) ? [...prevValue.newestManuscripts.items, ...newestManuscriptsLoaded.results] : newestManuscriptsLoaded.results,
				nextPage: newestManuscriptsLoaded.next,
				total: newestManuscriptsLoaded.count
			}
		}));
	}, []);

	const loadToPublishManuscripts = useCallback(async (
		sortField: ElasticSearchSort,
		sortValue: 'asc'|'desc',
		searchType: ElasticSearchType,
		searchValue: string,
		filters: SearchFilters,
		page = 1,
		perPage = 4
	) => {
		const toPublishManuscriptsLoaded = await PublisherService.getBooksPropositionsSearch(
			sortField,
			sortValue,
			searchType,
			searchValue,
			filters,
			page,
			perPage,
			[ApplicationStatusEnum.AutWant, ApplicationStatusEnum.PubWant]
		);

		setBooksData(prevValue => ({
			...prevValue,
			toPublishManuscripts: {
				items: (page > 1) ? [...prevValue.toPublishManuscripts.items, ...toPublishManuscriptsLoaded.results] : toPublishManuscriptsLoaded.results,
				nextPage: toPublishManuscriptsLoaded.next,
				total: toPublishManuscriptsLoaded.count
			}
		}));
	}, []);

	const loadBookmarkManuscripts = useCallback(async (
		sortField: ElasticSearchSort,
		sortValue: 'asc'|'desc',
		searchType: ElasticSearchType,
		searchValue: string,
		filters: SearchFilters,
		page = 1,
		perPage = 4
	) => {
		const bookmarkManuscriptsLoaded = await PublisherService.getBooksPropositionsSearch(
			sortField,
			sortValue,
			searchType,
			searchValue,
			filters,
			page,
			perPage,
			[],
			true
		);

		setBooksData(prevValue => ({
			...prevValue,
			bookmarkManuscripts: {
				items: (page > 1) ? [...prevValue.bookmarkManuscripts.items, ...bookmarkManuscriptsLoaded.results] : bookmarkManuscriptsLoaded.results,
				nextPage: bookmarkManuscriptsLoaded.next,
				total: bookmarkManuscriptsLoaded.count
			}
		}));
	}, []);

	const loadNotPublishManuscripts = useCallback(async (
		sortField: ElasticSearchSort,
		sortValue: 'asc'|'desc',
		searchType: ElasticSearchType,
		searchValue: string,
		filters: SearchFilters,
		page = 1,
		perPage = 4
	) => {
		const notPublishManuscriptsLoaded = await PublisherService.getBooksPropositionsSearch(
			sortField,
			sortValue,
			searchType,
			searchValue,
			filters,
			page,
			perPage,
			[ApplicationStatusEnum.AutNotWant, ApplicationStatusEnum.PubNotWant, ApplicationStatusEnum.Failed]
		);

		setBooksData(prevValue => ({
			...prevValue,
			notPublishManuscripts: {
				items: (page > 1) ? [...prevValue.notPublishManuscripts.items, ...notPublishManuscriptsLoaded.results] : notPublishManuscriptsLoaded.results,
				nextPage: notPublishManuscriptsLoaded.next,
				total: notPublishManuscriptsLoaded.count
			}
		}));
	}, []);

	const loadManuscripts = useCallback(async (
		sortField: ElasticSearchSort,
		sortValue: 'asc'|'desc',
		searchType: ElasticSearchType,
		searchValue: string,
		filters: SearchFilters,
		page = 1,
		perPage = 12
	) => {
		dispatch(toggleLoader(true));

		await loadNewestManuscripts(sortField, sortValue, searchType, searchValue, filters, page, perPage);
		await loadToPublishManuscripts(sortField, sortValue, searchType, searchValue, filters, page, perPage);
		await loadBookmarkManuscripts(sortField, sortValue, searchType, searchValue, filters, page, perPage);
		await loadNotPublishManuscripts(sortField, sortValue, searchType, searchValue, filters, page, perPage);

		setLoaded(true);
		dispatch(toggleLoader(false));
	}, [dispatch, loadNewestManuscripts, loadToPublishManuscripts, loadBookmarkManuscripts, loadNotPublishManuscripts]);

	const loadNextManuscripts = async (type: 'newest'|'toPublish'|'bookmark'|'notPublish') => {
		dispatch(toggleLoader(true));

		if (type === 'newest') {
			await loadNewestManuscripts(
				lastSearchData.sortField,
				lastSearchData.sortValue,
				lastSearchData.searchType,
				lastSearchData.searchValue,
				lastSearchData.filters,
				booksData.newestManuscripts.nextPage,
				12);
		} else if (type === 'toPublish') {
			await loadToPublishManuscripts(
				lastSearchData.sortField,
				lastSearchData.sortValue,
				lastSearchData.searchType,
				lastSearchData.searchValue,
				lastSearchData.filters,
				booksData.toPublishManuscripts.nextPage,
				12
			);
		} else if (type === 'bookmark') {
			await loadBookmarkManuscripts(
				lastSearchData.sortField,
				lastSearchData.sortValue,
				lastSearchData.searchType,
				lastSearchData.searchValue,
				lastSearchData.filters,
				booksData.bookmarkManuscripts.nextPage,
				12
			);
		} else if (type === 'notPublish') {
			await loadNotPublishManuscripts(
				lastSearchData.sortField,
				lastSearchData.sortValue,
				lastSearchData.searchType,
				lastSearchData.searchValue,
				lastSearchData.filters,
				booksData.notPublishManuscripts.nextPage,
				12
			);
		}

		dispatch(toggleLoader(false));
	}

	useEffect(() => {
		loadManuscripts(
			lastSearchData.sortField,
			lastSearchData.sortValue,
			lastSearchData.searchType,
			lastSearchData.searchValue,
			lastSearchData.filters
		);
	}, [loadManuscripts]);

	const loadDataFromSearch = (
		sortField: ElasticSearchSort,
		sortValue: 'asc'|'desc',
		searchType: ElasticSearchType,
		searchValue: string,
		filters: SearchFilters
	) => {
		setLastSearchData({
			sortField: sortField,
			sortValue: sortValue,
			searchType: searchType,
			searchValue: searchValue,
			filters: filters
		});

		loadManuscripts(
			sortField,
			sortValue,
			searchType,
			searchValue,
			filters,
			1,
			4
		);
	}

	return (
		<Grid container direction={"column"} spacing={2}>

			<SearchForElastic
				loadData={loadDataFromSearch}
				searchTypeOptions={[ElasticSearchType.searchTypeTitle, ElasticSearchType.searchTypeAuthor, ElasticSearchType.searchTypePublisher, ElasticSearchType.searchTypeIsbn]}
				sortTypeOptions={[ElasticSearchSort.submittedDate, ElasticSearchSort.matching]}
				filterOptions={[ElasticFilterType.tags, ElasticFilterType.categories, ElasticFilterType.countries, ElasticFilterType.languages]}
				showSort={true}
				showFilter={true}
			/>

			<Grid item>
				<Tabs value={tabActive} onChange={(event, newValue) => { setTabActive(newValue) }}>
					<Tab label={t('tabs.newProposals') + ' (' + (booksData.newestManuscripts?.total || 0) + ')'}/>
					<Tab label={t('tabs.Saved') + ' (' + (booksData.bookmarkManuscripts?.total || 0) + ')'}/>
					<Tab label={t('tabs.toPublish') + ' (' + (booksData.toPublishManuscripts?.total || 0) + ')'}/>
					<Tab label={t('tabs.rejected') + ' (' + (booksData.notPublishManuscripts?.total || 0) + ')'}/>
				</Tabs>

				<CustomTabPanel value={tabActive} index={0}>
					<Grid item container direction={"row"} spacing={2}>
						{booksData.newestManuscripts?.items?.map((application: PublisherBookPropositionListModel, index) => (
							<BookPropositionList
								id={application.id}
								key={index}
								book={application.publishedbook}
								sentBy={application.publisherfrom}
								status={application.status}
								submitted_date={application.submitted_date}
								matching={application.matching}
							/>
						))}
					</Grid>

					{booksData.newestManuscripts?.nextPage &&
                        <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                            <StyledButton type={"button"} color={"transparent"} onClick={() => {loadNextManuscripts('newest')}}>
								{t('allProposals.loadNext')}
                            </StyledButton>
                        </Grid>
					}

					{booksData.newestManuscripts?.items?.length === 0 && loaded &&
                        <EmptyBookShort
                            title={t('allProposals.empty')}
                            description={''}
                            showButton={false}
                        />
					}
				</CustomTabPanel>

				<CustomTabPanel value={tabActive} index={1}>
					<Grid item container direction={"row"} spacing={2}>
						{booksData.bookmarkManuscripts?.items?.map((application: PublisherBookPropositionListModel, index) => (
							<BookPropositionList
								id={application.id}
								key={index}
								book={application.publishedbook}
								sentBy={application.publisherfrom}
								status={application.status}
								submitted_date={application.submitted_date}
								matching={application.matching}
							/>
						))}
					</Grid>

					{booksData.bookmarkManuscripts?.nextPage &&
                        <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                            <StyledButton type={"button"} color={"transparent"} onClick={() => {loadNextManuscripts('bookmark')}}>
								{t('allProposals.loadNext')}
                            </StyledButton>
                        </Grid>
					}

					{booksData.bookmarkManuscripts?.items?.length === 0 && loaded &&
                        <EmptyBookShort
                            title={t('allProposals.empty')}
                            description={''}
                            showButton={false}
                        />
					}
				</CustomTabPanel>

				<CustomTabPanel value={tabActive} index={2}>
					<Grid item container direction={"row"} spacing={2}>
						{booksData.toPublishManuscripts?.items?.map((application: PublisherBookPropositionListModel, index) => (
							<BookPropositionList
								id={application.id}
								key={index}
								book={application.publishedbook}
								sentBy={application.publisherfrom}
								status={application.status}
								submitted_date={application.submitted_date}
								matching={application.matching}
							/>
						))}
					</Grid>

					{booksData.toPublishManuscripts?.nextPage &&
                        <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                            <StyledButton type={"button"} color={"transparent"} onClick={() => {loadNextManuscripts('toPublish')}}>
								{t('allProposals.loadNext')}
                            </StyledButton>
                        </Grid>
					}

					{booksData.toPublishManuscripts?.items?.length === 0 && loaded &&
                        <EmptyBookShort
                            title={t('allProposals.empty')}
                            description={''}
                            showButton={false}
                        />
					}
				</CustomTabPanel>

				<CustomTabPanel value={tabActive} index={3}>
					<Grid item container direction={"row"} spacing={2}>
						{booksData.notPublishManuscripts?.items?.map((application: PublisherBookPropositionListModel, index) => (
							<BookPropositionList
								id={application.id}
								key={index}
								book={application.publishedbook}
								sentBy={application.publisherfrom}
								status={application.status}
								submitted_date={application.submitted_date}
								matching={application.matching}
							/>
						))}
					</Grid>

					{booksData.notPublishManuscripts?.nextPage &&
                        <Grid container direction={"row"} justifyContent={"center"} style={{marginTop: '30px'}}>
                            <StyledButton type={"button"} color={"transparent"} onClick={() => {loadNextManuscripts('notPublish')}}>
								{t('allProposals.loadNext')}
                            </StyledButton>
                        </Grid>
					}

					{booksData.notPublishManuscripts?.items?.length === 0 && loaded &&
                        <EmptyBookShort
                            title={t('allProposals.empty')}
                            description={''}
                            showButton={false}
                        />
					}
				</CustomTabPanel>
			</Grid>
		</Grid>
	);
}

export default PublisherBooksPropositions;

interface BooksData {
	newestManuscripts: BooksListData,
	toPublishManuscripts: BooksListData,
	bookmarkManuscripts: BooksListData,
	notPublishManuscripts: BooksListData
}

interface BooksListData {
	items: PublisherBookPropositionListModel[],
	nextPage: number,
	total: number
}
